import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, MessageCircle, MessagesSquare, Pencil, Sparkles, X } from "lucide-react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { Input } from "./ui/input";
import { useToasts } from "./ui/toast";
import { api, ApiError } from "@/lib/api";
import { dollars } from "@/lib/money";
import { relTime, relTimeLong } from "@/lib/time";
import type { DraftPending } from "@/lib/types";
import { cn } from "@/lib/utils";

interface Props {
  draft: DraftPending;
  /** When focused (eg via keyboard nav), cmd-A approves and cmd-D denies. */
  focused?: boolean;
  onAfterAction?: () => void;
}

export function DraftCard({ draft, focused, onAfterAction }: Props) {
  const qc = useQueryClient();
  const { push } = useToasts();
  const [editing, setEditing] = React.useState(false);
  const [chunks, setChunks] = React.useState<string[]>(draft.chunks);
  const [denyOpen, setDenyOpen] = React.useState(false);
  const [denyReason, setDenyReason] = React.useState("");

  const invalidate = React.useCallback(() => {
    qc.invalidateQueries({ queryKey: ["drafts"] });
    qc.invalidateQueries({ queryKey: ["triage"] });
  }, [qc]);

  const onApiError = (e: unknown) => {
    const m = e instanceof ApiError ? e.message : (e as Error).message;
    push({ tone: "danger", title: "Action failed", description: m });
  };

  const approve = useMutation({
    mutationFn: () => api.approveDraft(draft.draft_id),
    onSuccess: ({ dispatched }) => {
      push({
        tone: "success",
        title: "Approved",
        description: `Dispatched ${dispatched} chunk${dispatched === 1 ? "" : "s"}.`,
      });
      invalidate();
      onAfterAction?.();
    },
    onError: onApiError,
  });

  const editApprove = useMutation({
    mutationFn: () => api.editDraft(draft.draft_id, chunks),
    onSuccess: () => {
      push({ tone: "success", title: "Edited and approved" });
      setEditing(false);
      invalidate();
      onAfterAction?.();
    },
    onError: onApiError,
  });

  const deny = useMutation({
    mutationFn: () => api.denyDraft(draft.draft_id, denyReason || "no reason"),
    onSuccess: () => {
      push({ tone: "warning", title: "Denied" });
      setDenyOpen(false);
      setDenyReason("");
      invalidate();
      onAfterAction?.();
    },
    onError: onApiError,
  });

  // keyboard shortcuts when focused
  React.useEffect(() => {
    if (!focused) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key.toLowerCase() === "a") {
        e.preventDefault();
        if (!approve.isPending && !editing) approve.mutate();
      }
      if (e.key.toLowerCase() === "d") {
        e.preventDefault();
        setDenyOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [approve, editing, focused]);

  const kindLabel: Record<DraftPending["kind"], { label: string; tone: "brand" | "warning" }> = {
    paid_send: { label: "Paid DM", tone: "warning" },
    member_reply: { label: "Member reply", tone: "brand" },
    recruiter_outreach: { label: "Recruiter outreach", tone: "warning" },
  };

  const cost = draft.estimated_cost_cents;

  return (
    <Card
      className={cn(
        "relative transition-shadow",
        focused && "shadow-[0_0_0_1.5px_hsl(var(--brand)/0.55)]",
      )}
    >
      <div className="flex items-center gap-2 border-b border-border/70 px-3 py-2 text-xs">
        <Badge tone={kindLabel[draft.kind].tone}>{kindLabel[draft.kind].label}</Badge>
        <span className="font-medium text-foreground">@{draft.creator_handle}</span>
        <span className="text-muted-foreground">→</span>
        <span className="font-mono text-muted-foreground">{draft.member_phone_redacted}</span>
        <span className="ml-auto flex items-center gap-2 text-muted-foreground">
          <Tooltip>
            <TooltipTrigger asChild>
              <span title={relTimeLong(draft.drafted_at)}>{relTime(draft.drafted_at)} old</span>
            </TooltipTrigger>
            <TooltipContent>{relTimeLong(draft.drafted_at)}</TooltipContent>
          </Tooltip>
          <span>·</span>
          <span title="Estimated cost">{dollars(cost)}</span>
        </span>
      </div>

      <div className="space-y-2 p-3">
        {draft.inbound_message ? (
          <div className="rounded-md border border-border/60 bg-muted/30 p-2 text-xs">
            <div className="mb-0.5 flex items-center gap-1 text-muted-foreground">
              <MessageCircle className="h-3 w-3" /> inbound
            </div>
            <div className="text-foreground/90">{draft.inbound_message}</div>
          </div>
        ) : null}

        {!editing ? (
          <div className="space-y-1.5">
            {draft.chunks.map((c, i) => (
              <div
                key={i}
                className="rounded-md border border-brand/30 bg-brand/10 px-3 py-2 text-sm leading-relaxed"
              >
                {c}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-1.5">
            {chunks.map((c, i) => (
              <Textarea
                key={i}
                value={c}
                onChange={(e) =>
                  setChunks((cur) => cur.map((x, j) => (j === i ? e.target.value : x)))
                }
                rows={2}
                className="text-sm"
              />
            ))}
            <div className="flex gap-1.5 pt-1">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setChunks((c) => [...c, ""])}
              >
                + chunk
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() =>
                  setChunks((c) => (c.length > 1 ? c.slice(0, -1) : c))
                }
              >
                – chunk
              </Button>
            </div>
          </div>
        )}

        {draft.system_prompt_summary ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex cursor-help items-center gap-1 text-2xs text-muted-foreground">
                <Sparkles className="h-3 w-3" />
                <span className="max-w-md truncate">{draft.system_prompt_summary}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-md">
              {draft.system_prompt_summary}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>

      <div className="flex items-center gap-1.5 border-t border-border/70 px-3 py-2">
        {!editing ? (
          <>
            <Button
              size="xs"
              onClick={() => approve.mutate()}
              disabled={approve.isPending}
            >
              <Check className="mr-1 h-3 w-3" /> Approve
              <span className="kbd ml-1.5">⌘A</span>
            </Button>
            <Button size="xs" variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="mr-1 h-3 w-3" /> Edit
            </Button>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => setDenyOpen((o) => !o)}
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <X className="mr-1 h-3 w-3" /> Deny
              <span className="kbd ml-1.5">⌘D</span>
            </Button>
            <span className="ml-auto text-2xs text-muted-foreground">
              <MessagesSquare className="mr-0.5 inline h-3 w-3" />
              {draft.chunks.length} chunk{draft.chunks.length === 1 ? "" : "s"}
            </span>
          </>
        ) : (
          <>
            <Button
              size="xs"
              onClick={() => editApprove.mutate()}
              disabled={editApprove.isPending}
            >
              <Check className="mr-1 h-3 w-3" /> Save & approve
            </Button>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => {
                setChunks(draft.chunks);
                setEditing(false);
              }}
            >
              cancel
            </Button>
          </>
        )}
      </div>

      {denyOpen ? (
        <div className="flex items-center gap-1.5 border-t border-border/70 bg-destructive/5 px-3 py-2">
          <Input
            placeholder="Reason (required)"
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            className="h-7 text-xs"
          />
          <Button
            size="xs"
            variant="destructive"
            disabled={!denyReason.trim() || deny.isPending}
            onClick={() => deny.mutate()}
          >
            confirm deny
          </Button>
          <Button size="xs" variant="ghost" onClick={() => setDenyOpen(false)}>
            cancel
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
